import { Auth } from "aws-amplify";
import {
  setLoggedIn,
  setAccessToken,
  setIdToken,
  setExpireBy,
  setUserEmail,
  getExpireBy,
  clearSession,
} from "../../utils/storage";

// Action Types
export const AUTH_LOGIN_REQUEST = 'AUTH_LOGIN_REQUEST';
export const AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS';
export const AUTH_LOGIN_FAILURE = 'AUTH_LOGIN_FAILURE';
export const AUTH_LOGOUT = 'AUTH_LOGOUT';
export const AUTH_CHECK_SESSION = 'AUTH_CHECK_SESSION';

// Action Creators
export const authLoginRequest = () => ({
  type: AUTH_LOGIN_REQUEST
});

export const authLoginSuccess = (authData) => ({
  type: AUTH_LOGIN_SUCCESS,
  payload: authData
});

export const authLoginFailure = (error) => ({
  type: AUTH_LOGIN_FAILURE,
  payload: error
});

export const authLogout = () => ({
  type: AUTH_LOGOUT
});

export const authCheckSession = (authData) => ({
  type: AUTH_CHECK_SESSION,
  payload: authData
});

// Thunk Actions
export const loginUser = (currentSession) => async (dispatch) => {
  dispatch(authLoginRequest());
  
  try {
    const authState = {
      user: currentSession,
      isAuthenticated: currentSession != null,
      idToken: currentSession != null ? currentSession.getIdToken().getJwtToken() : null,
      accessToken: currentSession != null ? currentSession.getAccessToken().getJwtToken() : null,
      expireBy: new Date().getTime() + currentSession.getIdToken().getExpiration() * 1000,
      userEmail: currentSession != null ? currentSession.getIdToken().payload.email : null,
    };

    // Update storage
    setLoggedIn(authState.isAuthenticated);
    setAccessToken(authState.accessToken);
    setIdToken(authState.idToken);
    setExpireBy(authState.expireBy);
    setUserEmail(authState.userEmail);

    dispatch(authLoginSuccess(authState));
    return authState;
  } catch (error) {
    dispatch(authLoginFailure(error.message));
    throw error;
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await Auth.signOut({ global: true });
  } catch (error) {
    console.log("error signing out: ", error);
  } finally {
    // Clear storage when logging out
    clearSession();
    dispatch(authLogout());
  }
};

export const checkAuthSession = () => async (dispatch) => {
  try {
    // First, check if stored expiration time has passed
    const storedExpireBy = getExpireBy();
    if (storedExpireBy && new Date().getTime() > parseInt(storedExpireBy)) {
      // Session expired, clear storage and logout
      clearSession();
      dispatch(authLogout());
      return false;
    }

    // Then check AWS Amplify session
    const currentSession = await Auth.currentSession();
    
    if (currentSession) {
      const authState = {
        user: currentSession,
        isAuthenticated: true,
        idToken: currentSession.getIdToken().getJwtToken(),
        accessToken: currentSession.getAccessToken().getJwtToken(),
        expireBy: new Date().getTime() + currentSession.getIdToken().getExpiration() * 1000,
        userEmail: currentSession.getIdToken().payload.email,
      };

      // Check if token is expired
      if (new Date() > new Date(authState.expireBy)) {
        // Session expired, clear storage and logout
        clearSession();
        dispatch(authLogout());
        return false;
      }

      // Update storage
      setLoggedIn(authState.isAuthenticated);
      setAccessToken(authState.accessToken);
      setIdToken(authState.idToken);
      setExpireBy(authState.expireBy);
      setUserEmail(authState.userEmail);

      dispatch(authCheckSession(authState));
      return true;
    } else {
      // No session found, clear storage and logout
      clearSession();
      dispatch(authLogout());
      return false;
    }
  } catch (error) {
    console.log("Error checking session:", error);
    // On error, clear storage and logout
    clearSession();
    dispatch(authLogout());
    return false;
  }
};
